import { extractMentionedSources } from '#pplx/frontend/apps/ask/ask-input/query-serialization/lexicalNodeToQuery.js';
import { useCallback, useEffect, useMemo, useRef } from 'react';
const normalizeMentionedSource = (source) => source.startsWith('source:') ? source.slice('source:'.length) : source;
const getUniqueMentionedSources = (inputJson) => Array.from(new Set(extractMentionedSources(inputJson?.root).map(normalizeMentionedSource))).filter((source) => source !== 'web');
const sourceArraysEqual = (left, right) => left.length === right.length && left.every((source, index) => source === right[index]);
export const useAskInputSourceSelection = ({ enabled = true, isComputer = false, sources, onSourcesChange: setSources, initialSources, isFollowUp, inputJson, getSourceLabel, areSourceLabelsLoading = false, onSourceMentionSelect, onSourceMentionDeselect, }) => {
    // Restore the inherited selection once so later edits remain caller-owned.
    const initialized = useRef(false);
    const previousInputJsonRef = useRef(undefined);
    const pendingMentionInsertionsRef = useRef(new Set());
    const handleSourceMentionSelect = useCallback((source, label) => {
        if (!onSourceMentionSelect || source === 'web') {
            return;
        }
        const pendingMentionInsertions = pendingMentionInsertionsRef.current;
        if (pendingMentionInsertions.has(source)) {
            return;
        }
        const didInsert = onSourceMentionSelect(source, label);
        if (didInsert !== false) {
            pendingMentionInsertions.add(source);
        }
    }, [onSourceMentionSelect]);
    const handleSourceMentionDeselect = useCallback((source) => {
        pendingMentionInsertionsRef.current.delete(source);
        onSourceMentionDeselect?.(source);
    }, [onSourceMentionDeselect]);
    useEffect(() => {
        if (initialized.current)
            return;
        if (!enabled || !isFollowUp || initialSources.length === 0) {
            return;
        }
        const sourceMentionsToInsert = initialSources.filter((source) => source !== 'web');
        if (areSourceLabelsLoading && sourceMentionsToInsert.length > 0) {
            return;
        }
        setSources([...initialSources]);
        sourceMentionsToInsert.forEach((source) => {
            handleSourceMentionSelect(source, getSourceLabel?.(source) ?? source);
        });
        initialized.current = true;
    }, [
        enabled,
        isFollowUp,
        initialSources,
        setSources,
        getSourceLabel,
        areSourceLabelsLoading,
        handleSourceMentionSelect,
    ]);
    useEffect(() => {
        if (enabled &&
            isFollowUp &&
            inputJson === undefined &&
            previousInputJsonRef.current !== undefined) {
            const sourceMentionsToInsert = sources.filter((source) => source !== 'web');
            if (areSourceLabelsLoading && sourceMentionsToInsert.length > 0) {
                return;
            }
            sourceMentionsToInsert.forEach((source) => {
                pendingMentionInsertionsRef.current.delete(source);
                handleSourceMentionSelect(source, getSourceLabel?.(source) ?? source);
            });
        }
    }, [
        enabled,
        isFollowUp,
        inputJson,
        sources,
        getSourceLabel,
        areSourceLabelsLoading,
        handleSourceMentionSelect,
    ]);
    useEffect(() => {
        if (!enabled || inputJson === undefined) {
            previousInputJsonRef.current = undefined;
            return;
        }
        const mentionedSources = getUniqueMentionedSources(inputJson);
        const previousMentionedSources = getUniqueMentionedSources(previousInputJsonRef.current);
        const removedSources = previousMentionedSources.filter((source) => !mentionedSources.includes(source));
        const shouldSyncMentionedSources = !sourceArraysEqual(previousMentionedSources, mentionedSources);
        const nextSources = [
            ...sources.filter((source) => !removedSources.includes(source)),
            ...(shouldSyncMentionedSources
                ? mentionedSources.filter((source) => !sources.includes(source))
                : []),
        ];
        if (!sourceArraysEqual(sources, nextSources)) {
            setSources(nextSources);
        }
        mentionedSources.forEach((source) => pendingMentionInsertionsRef.current.delete(source));
        removedSources.forEach((source) => pendingMentionInsertionsRef.current.delete(source));
        const sourceMentionsToInsert = nextSources.filter((source) => source !== 'web' && !mentionedSources.includes(source) && !removedSources.includes(source));
        if (isFollowUp && !(areSourceLabelsLoading && sourceMentionsToInsert.length > 0)) {
            sourceMentionsToInsert.forEach((source) => handleSourceMentionSelect(source, getSourceLabel?.(source) ?? source));
        }
        previousInputJsonRef.current = inputJson;
    }, [
        enabled,
        isFollowUp,
        inputJson,
        sources,
        setSources,
        getSourceLabel,
        areSourceLabelsLoading,
        handleSourceMentionSelect,
    ]);
    const followUpOmittedSources = useCallback((source) => source.type !== 'remote_mcp' && source.type !== 'local_mcp', []);
    const omittedSources = isFollowUp ? followUpOmittedSources : undefined;
    const plusMenuSourcesProps = useMemo(() => {
        if (enabled) {
            return {
                showSources: true,
                sources,
                onSourcesChange: setSources,
                onSourceMentionSelect: handleSourceMentionSelect,
                onSourceMentionDeselect: handleSourceMentionDeselect,
                omittedSources,
                isComputer,
            };
        }
        return { showSources: false };
    }, [
        enabled,
        sources,
        setSources,
        handleSourceMentionSelect,
        handleSourceMentionDeselect,
        omittedSources,
        isComputer,
    ]);
    return { plusMenuSourcesProps };
};
//# sourceMappingURL=useAskInputSourceSelection.js.map